/**
 * Interface for polling item configuration
 */
interface PollingItem {
  pollFn: () => void | Promise<void>;
  intervalSeconds: number;
  active: boolean;
  timeoutId?: NodeJS.Timeout;
}

/**
 * Type for the polling items registry
 */
type PollingRegistry = Record<string, PollingItem>;

/**
 * Private registry for storing polling items
 */
const items: PollingRegistry = {};

/**
 * Register new polling.
 * @param pollId - ID of the poll
 * @param pollFn - callback for setInterval
 * @param intervalSeconds - interval in seconds
 */
export function register(
  pollId: string,
  pollFn: () => void | Promise<void>,
  intervalSeconds: number
): void {
  if (items[pollId] !== undefined) {
    throw new Error(`Can't register polling: Polling with pollID:${pollId} is already registered`);
  }

  items[pollId] = {
    pollFn,
    intervalSeconds,
    active: false
  };

  console.log(`Polling with pollID:${pollId} registered with interval: ${intervalSeconds}`);
}

/**
 * Check if particular pollId is running or not
 * @param pollId - The poll ID to check
 * @returns true if running, false otherwise
 */
export function isRunning(pollId: string): boolean {
  const item = items[pollId];
  return item !== undefined && item.active;
}

/**
 * Start polling interval. Will trigger an exception if polling is undefined.
 * This will execute the polling function immediately and then at each interval.
 * @param pollId - The poll ID to start
 */
export function start(pollId: string): void {
  const pollItem = items[pollId];
  if (!pollItem) {
    throw new Error(`Can't start polling: Polling with pollID:${pollId} is not registered`);
  }

  const timeoutMs = pollItem.intervalSeconds * 1000;
  if (isRunning(pollId)) {
    throw new Error(`Attempt to trigger multiple polling for the same pollID:${pollId}`);
  }

  pollItem.active = true;

  // Define the polling execution function
  const executePollFn = () => {
    if (pollItem.timeoutId) {
      clearTimeout(pollItem.timeoutId);
    }

    const resetTimeout = () => {
      // Check active status again after wait completes (in case stop() was called during wait)
      // then, break recursion if not active.
      if (!items[pollId]?.active) {
        return;
      }

      pollItem.timeoutId = setTimeout(executePollFn, timeoutMs);
    };

    // Run the polling function and handle both sync and async cases
    const result = pollItem.pollFn();
    // Check if pollFn returns a Promise (i.e., is async)
    if (result instanceof Promise) {
      // Async function - wait for it to complete before setting next timeout
      result
        .catch((error) => {
          console.error(`Error in pollID:${pollId} -`, error);
        })
        .finally(resetTimeout);
    } else {
      // Synchronous function completed successfully
      resetTimeout();
    }
  };

  executePollFn();
}

/**
 * Stop polling interval
 * @param pollId - The poll ID to stop
 * @returns false if polling was not found or already stopped, true otherwise
 */
export function stop(pollId: string): boolean {
  const pollItem = items[pollId];
  if (!pollItem || !pollItem.active) {
    return false;
  }

  clearTimeout(pollItem.timeoutId);

  pollItem.timeoutId = undefined;
  pollItem.active = false;
  return true;
}

/**
 * De-register existing pollID
 * @param pollId - The poll ID to deregister
 */
export function deregister(pollId: string): void {
  if (items[pollId] === undefined) {
    throw new Error(`Can't reset polling: Polling with pollID:${pollId} is not registered`);
  }

  if (items[pollId].timeoutId) {
    clearTimeout(items[pollId].timeoutId);
  }

  items[pollId].active = false;
  // Use Reflect.deleteProperty for safer property deletion
  Reflect.deleteProperty(items, pollId);
}

/**
 * Get all registered poll IDs
 * @returns Array of registered poll IDs
 */
export function getRegisteredPollIds(): string[] {
  return Object.keys(items);
}

/**
 * Get the status of a specific poll
 * @param pollId - The poll ID to get status for
 * @returns Poll status information or undefined if not registered
 */
export function getPollStatus(pollId: string): { isRunning: boolean; intervalSeconds: number } | undefined {
  const pollItem = items[pollId];
  if (pollItem === undefined) return undefined;

  return { isRunning: pollItem.active, intervalSeconds: pollItem.intervalSeconds };
}

/**
 * Stop and deregister all polling operations
 */
export function cleanup(): void {
  const pollIds = getRegisteredPollIds();

  for (const pollId of pollIds) {
    try {
      deregister(pollId);
    } catch (error) {
      console.warn(`Error cleaning up poll ${pollId}:`, error);
    }
  }
}

/**
 * Default export for backward compatibility with class-style imports
 */
const PollingManager = {
  register,
  isRunning,
  start,
  stop,
  deregister,
  getRegisteredPollIds,
  getPollStatus,
  cleanup
};

export default PollingManager;
